import { BaseEntityDto } from "@/core/common/entities/base-entity.dto";
import { UserDto } from "@/core/features/users/user.dto";
import { Transform, Type } from "class-transformer";
import { QualificationDto } from "@/core/features/qualifications/qualification.dto";
import { parseApiDate } from "@/utils/dates/dateFormats";
import { getToday, isDateAfter, toLocaleTimezone } from "@/utils/dates/dateUtils";
import { addYears, subMonths } from "date-fns";
import { DateFormatterService } from "@/core/common/date-formatter/date-formatter.service";
import { toHours, toEuros } from "@/utils/numberUtils";
import { UserHasQualificationStatusEnum, UserHasQualificationTransitionTypeEnum } from "@altertec_gparn/lib";
import { resolveUserHasQualificationEffectiveCost } from "@/core/features/users-have-qualifications/resolve-user-has-qualification-effective-cost";
import { SupplierDto } from "@/core/features/suppliers/supplier.dto";

export class UserHasQualificationDto extends BaseEntityDto {
  @Type(() => UserDto)
  user: UserDto;
  userId: string;

  @Type(() => QualificationDto)
  qualification: QualificationDto;
  qualificationId: string;

  @Transform(({ value }) => toLocaleTimezone(parseApiDate(value)))
  start: Date;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  effectiveDate?: Date;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  finishedAt?: Date;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  end?: Date;

  @Transform(({ value }) => toHours(value))
  collectiveAgreementHours: number;

  @Transform(({ value }) => (value != null ? toEuros(value) : value))
  cost?: number;

  @Transform(({ value }) => (value != null ? toEuros(value) : value))
  internalCost?: number;

  paidByEmployee: boolean;

  invoiceNumber?: string;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  invoiceDate?: Date;

  supplierId?: string;

  @Type(() => SupplierDto)
  supplier?: SupplierDto;

  subsidized: boolean;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  paymentDate?: Date;

  @Transform(({ value }) => (value != null ? toEuros(value) : value))
  subsidyAmount?: number;

  @Transform(({ value }) => (value ? toLocaleTimezone(parseApiDate(value)) : null))
  subsidyMonth?: Date;

  status: UserHasQualificationStatusEnum;

  certified: boolean;

  archived: boolean;
  previousUserHasQualificationId?: string;
  transitionType?: UserHasQualificationTransitionTypeEnum;

  get imputationStart(): Date {
    return this.effectiveDate ?? this.start;
  }

  get isImputationPending(): boolean {
    if (!this.imputationStart) return true;

    return isDateAfter(new Date(this.imputationStart), getToday(), true);
  }

  get durationInYearsIsExceeded() {
    return addYears(this.imputationStart, this.qualification.durationInYears).getTime() <= getToday().getTime();
  }

  get priceHour(): number {
    if (this.archived) return 0;
    if (!this.collectiveAgreementHours) return null;
    if (this.isImputationPending || this.isExpired || (!this.end && this.durationInYearsIsExceeded)) return 0;
    const effectiveCost: number = resolveUserHasQualificationEffectiveCost({
      cost: this.cost,
      internalCost: this.internalCost,
      qualificationCost: this.qualification.cost,
      paidByEmployee: this.paidByEmployee,
    });

    return effectiveCost / (this.collectiveAgreementHours * this.qualification.durationInYears);
  }

  get isExpired(): boolean {
    return this.end && isDateAfter(getToday(), this.end, true);
  }

  get isNearExpiration(): boolean {
    return this.end && isDateAfter(getToday(), subMonths(this.end, 1), true);
  }

  get expirationInfo(): string {
    if (!this.end) return "No caduca";

    return `${this.isExpired ? "Caducó" : "Caduca"} el ${new DateFormatterService().formatDate(this.end)}`;
  }
}
